import { useEffect, useState } from 'react';
import { aiClient, AiBudgetExhaustedError } from '../lib/aiClient.js';

const CATEGORIES = ['Unmanned', 'Manned', 'Airspace', 'AI', 'Manufacturing'];

// Bench → Today draft preview. Mirrors AddSourceModal step 2:
//   1. On mount, call /api/ai/rewrite-bench (active stage_4_draft prompt) with
//      the bench item's source content.
//   2. Show the AI-drafted headline / summary / takeaway / category as editable
//      fields so the editor can adjust before insertion.
//   3. "Add to brief" promotes the bench item with the edited article as an
//      override — the server inserts the row exactly as previewed.
export default function BenchArticleDraftPreview({
  item,
  isoDate,
  onSubmit,
  onBack,
  onCancel,
  onBudgetExhausted,
}) {
  const [drafting, setDrafting] = useState(true);
  const [draftError, setDraftError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [meta, setMeta] = useState(null); // { promptName, promptVersion, model, latencyMs }
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setDrafting(true);
      setDraftError(null);
      try {
        const res = await aiClient.rewriteBench({
          // articleId is required by the route — the bench item's ObjectId is a
          // valid stand-in for usage logging / idempotency keying. The article
          // hasn't been inserted into the edition yet at this point.
          articleId: String(item._id),
          isoDate,
          article: {
            title: item.title || '',
            summary: item.summary || '',
            body: item.summary || '',
            sourceUrl: item.sourceUrl || '',
            sourceName: item.source || '',
            category: item.category || '',
            articleType: item.pipelineMeta?.article_type || '',
            publishedDate: item.pipelineMeta?.published_date || '',
            xtiPortfolioFlag: item.pipelineMeta?.xti_portfolio_flag === true,
            draftSnippet: item.draftSnippet || '',
          },
        });
        if (cancelled) return;
        setDraft({
          headline: res.headline || item.title || '',
          summary: res.summary || item.summary || '',
          keyTakeaway: res.keyTakeaway || item.takeaway || '',
          category: res.category || item.category || '',
          primaryCompany: '',
          imageUrl: item.pipelineMeta?.image_url || item.pipelineMeta?.imageUrl || '',
          keyClaims: Array.isArray(res.claimInventory)
            ? res.claimInventory.map((c) => (typeof c === 'string' ? c : c?.claim || ''))
            : [],
          imageSearchKeywords: Array.isArray(res.imageSearchKeywords)
            ? res.imageSearchKeywords
            : [],
        });
        setMeta({
          promptName: res.promptName,
          promptVersion: res.promptVersion,
          model: res.model,
          latencyMs: res.latencyMs,
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof AiBudgetExhaustedError) {
          onBudgetExhausted?.();
          return;
        }
        setDraftError(err.message || 'Pipeline draft failed.');
      } finally {
        if (!cancelled) setDrafting(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
    // We intentionally only depend on the bench item's id — re-running on every
    // pipelineMeta tick would re-bill the AI call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item._id]);

  function updateDraft(field, value) {
    setDraft((d) => ({ ...(d || {}), [field]: value }));
  }

  async function handleSubmit() {
    if (!draft) return;
    setSaveError(null);
    setSaving(true);
    try {
      await onSubmit({
        headline: draft.headline?.trim() || '',
        summary: draft.summary?.trim() || '',
        keyTakeaway: draft.keyTakeaway?.trim() || '',
        category: draft.category || undefined,
        primaryCompany: draft.primaryCompany?.trim() || undefined,
        imageUrl: draft.imageUrl?.trim() || undefined,
        source: item.sourceUrl
          ? { url: item.sourceUrl, displayName: item.source || undefined }
          : undefined,
      });
    } catch (err) {
      setSaveError(err.message || 'Add to brief failed.');
    } finally {
      setSaving(false);
    }
  }

  if (drafting) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            disabled
            className="hxd-btn hxd-btn-ghost hxd-btn-sm opacity-40 cursor-not-allowed"
          >
            ← Back to bench
          </button>
          <span className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-orange">
            Drafting · stage_4_draft
          </span>
        </div>

        <div className="border border-hxd-border-soft rounded-[3px] bg-white p-8 text-center">
          <div
            aria-hidden="true"
            className="inline-block w-2.5 h-2.5 rounded-full bg-hxd-orange animate-pulse mb-3"
          />
          <div className="font-display text-[12px] font-bold tracking-display-md uppercase text-hxd-ink mb-1">
            Wingman is drafting this article
          </div>
          <p className="text-[12px] text-hxd-muted">
            Running the pipeline stage_4_draft prompt against the source content. This
            usually takes 5–10 seconds.
          </p>
        </div>
      </div>
    );
  }

  if (draftError) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="hxd-btn hxd-btn-ghost hxd-btn-sm"
          >
            ← Back to bench
          </button>
        </div>
        <div className="border border-red-200 bg-red-50 rounded-[3px] p-4 text-[12px] text-hxd-err">
          <div className="font-display text-[11px] font-bold tracking-display-md uppercase mb-1">
            Draft failed
          </div>
          <p>{draftError}</p>
        </div>
        <div className="flex items-center justify-end gap-2 pt-3">
          <button
            type="button"
            onClick={onCancel}
            className="hxd-btn hxd-btn-ghost hxd-btn-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onBack}
            className="hxd-btn hxd-btn-primary hxd-btn-sm"
          >
            Back to bench
          </button>
        </div>
      </div>
    );
  }

  if (!draft) return null;

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={onBack}
          className="hxd-btn hxd-btn-ghost hxd-btn-sm"
        >
          ← Back to bench
        </button>
        <span className="font-display text-[10px] font-bold tracking-display-md uppercase text-hxd-muted">
          Step 2 · Review draft
        </span>
      </div>

      <div className="space-y-4">
        <div className="text-[11px] text-hxd-muted flex flex-wrap gap-2 items-center">
          <span>From:</span>
          {item.sourceUrl ? (
            <a
              href={item.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="text-hxd-orange hover:underline break-all"
            >
              {item.source || item.sourceUrl}
            </a>
          ) : (
            <span>{item.source || '(unknown source)'}</span>
          )}
          {meta?.promptName && (
            <span className="ml-auto text-[11px] text-hxd-muted">
              Drafted with {meta.promptName} v{meta.promptVersion}
            </span>
          )}
        </div>

        <div>
          <label className="hxd-label">Headline</label>
          <input
            className="hxd-input font-semibold"
            value={draft.headline || ''}
            onChange={(e) => updateDraft('headline', e.target.value)}
          />
        </div>

        <div>
          <label className="hxd-label">Summary</label>
          <textarea
            className="hxd-input min-h-[140px]"
            rows={6}
            value={draft.summary || ''}
            onChange={(e) => updateDraft('summary', e.target.value)}
          />
        </div>

        <div>
          <label className="hxd-label">Key Takeaway</label>
          <input
            className="hxd-input"
            value={draft.keyTakeaway || ''}
            onChange={(e) => updateDraft('keyTakeaway', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="hxd-label">Category</label>
            <select
              className="hxd-input"
              value={draft.category || ''}
              onChange={(e) => updateDraft('category', e.target.value)}
            >
              <option value="">— Choose —</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="hxd-label">Primary Company</label>
            <input
              className="hxd-input"
              value={draft.primaryCompany || ''}
              onChange={(e) => updateDraft('primaryCompany', e.target.value)}
              placeholder="(optional)"
            />
          </div>
        </div>

        {draft.imageUrl && (
          <div>
            <label className="hxd-label">Image (from pipeline)</label>
            <div className="flex items-start gap-3">
              <img
                src={draft.imageUrl}
                alt=""
                className="w-32 h-20 object-cover border border-hxd-border-soft rounded-sm"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <input
                className="hxd-input flex-1"
                value={draft.imageUrl || ''}
                onChange={(e) => updateDraft('imageUrl', e.target.value)}
              />
            </div>
          </div>
        )}

        {draft.keyClaims.length > 0 && (
          <div>
            <div className="hxd-label">Key claims (verify-ready)</div>
            <ul className="list-disc pl-5 text-[12px] text-hxd-muted space-y-1">
              {draft.keyClaims.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>
        )}

        {saveError && (
          <div className="text-[12px] text-hxd-err">{saveError}</div>
        )}

        <div className="flex items-center justify-between gap-2 pt-3 border-t border-hxd-border-soft">
          <button
            type="button"
            onClick={onBack}
            className="hxd-btn hxd-btn-ghost hxd-btn-sm"
          >
            ← Back
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="hxd-btn hxd-btn-ghost hxd-btn-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving || !draft.headline?.trim() || !draft.summary?.trim()}
              className="hxd-btn hxd-btn-primary hxd-btn-sm"
            >
              {saving ? 'Adding…' : 'Add to brief'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
